import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import styled from 'styled-components';

import useCountryName from '@hooks/CachedLists/UseCountryName';

import { NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import { getAddressText, getCompactAddressText } from '@components/Addresses/AddressUtils';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const AddressLineContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    line-height: 1.3em;
    gap: 1.5rem;

    &.map-link-below {
        display: flex;
        flex-direction: column;
        justify-content: inherit;
        align-items: inherit;
        gap: 0;
        min-height: 100%;

        .address-line-content {
            flex: 1;
        }

        .icon-container-link {
            margin: 0;
        }

        .map-link-footer {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            text-align: center;
            border-top: ${(props) => props.theme.DefaultBorder};
            font-size: ${(props) => props.theme.FontSizeSm};
            text-transform: uppercase;

            a,
            button {
                font-size: inherit;
                text-transform: inherit;
            }
        }
    }

    &.disabled {
        opacity: 0.5;
    }

    .icon-container {
        margin: 0.5rem 0 0 1rem;
        font-size: 1.3em;
    }

    &.compact {
        gap: 0.5rem;
        cursor: pointer;
        font-weight: 600;
        line-height: 1.1em;
        justify-content: flex-end;

        &.disabled {
            font-weight: normal;
        }

        .icon-container {
            margin: 0;
            font-size: inherit;
        }
    }
`;

const AddressLine = ({ address, disableMap, noIcon, onClick, twoLines, compact, inLocationSelector, showAccess = true }) => {
    const countryForName = address && address.Country ? address.Country : '';
    const countryName = useCountryName(countryForName);

    const ComposeAddressForMaps = () => {
        const addressString = [address.Street, address.City, address.Zip, address.State, countryName]
            .filter(Boolean)
            .join('+');
        return 'https://www.google.com/maps/place/' + encodeURIComponent(addressString);
    };

    const showMap = (event) => {
        event.stopPropagation();
        window.open(ComposeAddressForMaps());
    };

    const noAddress = () => {
        if (!address) {
            return true;
        }
        return (
            (!address.Name || address.Name.trim() === '') &&
            (!address.Street || address.Street.trim() === '') &&
            (!address.City || address.City.trim() === '') &&
            (!address.Zip || address.Zip.trim() === '') &&
            (!address.State || address.State.trim() === '') &&
            (!address.Country || address.Country.trim() === '')
        );
    };

    let stylefordiv = {};
    if (noIcon && !disableMap) {
        stylefordiv = { cursor: 'pointer' };
    }

    if (compact) {
        return (
            <AddressLineContainer
                className={`address-line-container compact${noAddress() ? ' disabled' : ''}${inLocationSelector ? ' map-link-below' : ''}`}
            >
                {!inLocationSelector && (
                    <div className="icon-container">
                        <NwIcon
                            pointer
                            icon={byPrefixAndName.fasds['map-location-dot']}
                            tooltip="Show me the location on Google Maps"
                            onClick={showMap}
                        />
                    </div>
                )}
                {noAddress() ? (
                    <div>no address</div>
                ) : (
                    <div className="address-line-content" onClick={showMap}>
                        {getCompactAddressText({ address, countryName, showAccess })}
                    </div>
                )}
                {inLocationSelector && !noAddress() && !disableMap && (
                    <div className="map-link-footer">
                        <NwLinkButton
                            label="Google Maps"
                            onClick={showMap}
                            icon={byPrefixAndName.fasds['map-location-dot']}
                        />
                    </div>
                )}
            </AddressLineContainer>
        );
    }

    return (
        <AddressLineContainer
            className={`address-line-container${noAddress() ? ' disabled' : ''}${inLocationSelector ? ' map-link-below' : ''}`}
        >
            {noAddress() ? (
                <div>no address</div>
            ) : (
                <>
                    {!disableMap && !noIcon && !inLocationSelector && (
                        <div className="icon-container">
                            <NwIcon
                                pointer
                                icon={byPrefixAndName.fasds['map-location-dot']}
                                tooltip="Show me the location on Google Maps"
                                onClick={showMap}
                            />
                        </div>
                    )}
                    <div
                        className="address-line-content"
                        style={stylefordiv}
                        onClick={noIcon && !disableMap ? showMap : onClick}
                    >
                        {ReactHtmlParser(getAddressText({ address, countryName, twoLines, showAccess }))}
                    </div>
                    {inLocationSelector && !noIcon && !disableMap && (
                        <div className="map-link-footer">
                            <NwLinkButton
                                label="Google Maps"
                                onClick={showMap}
                                icon={byPrefixAndName.fasds['map-location-dot']}
                            />
                        </div>
                    )}
                </>
            )}
        </AddressLineContainer>
    );
};

export default AddressLine;
